import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
  Paper,
  Breadcrumbs,
  Link,
  Slider,
  Fade,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Restaurant as RestaurantIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, FILE_BASE_URL } from '../config';
import { getFileUrl } from '../utils/urlHelper';

interface Paciente {
  id: string;
  nombre: string;
  dni: string;
  genero: string;
  profesion: string;
  obra_social: string;
  plan: string;
  nro_socio: string;
  fecha_nacimiento: string;
  edad: number;
  altura_cm: number;
  peso_kg: number;
  religion: string;
  familia: string;
  email: string;
  email2: string;
  ruta_imagen_perfil: string;
  ultima_modificacion?: string;
  medico_id: string;
}

interface Estadisticas {
  total: number;
  activos: number;
  nuevos: number;
  conEmails: number;
}

const Pacientes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const generoFiltro = params.get('genero');
  
  // Estados principales
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState<string>('');
  
  // Estados de filtros
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [filtrosAvanzados, setFiltrosAvanzados] = useState(false);
  const [filtros, setFiltros] = useState({
    obraSocial: '',
    genero: '',
    rangoEdad: [0, 100],
    dni: '',
    profesion: '',
    religion: '',
  });
  
  // Estados de ordenamiento
  const [sortBy, setSortBy] = useState('nombre');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Estados de diálogos
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState<Paciente | null>(null);

  // Cargar pacientes
  useEffect(() => {
    fetchPacientes();
  }, []);

  const fetchPacientes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/pacientes`);
      const pacientesOrdenados = response.data.sort((a: Paciente, b: Paciente) => {
        const fechaA = a.ultima_modificacion ? new Date(a.ultima_modificacion).getTime() : 0;
        const fechaB = b.ultima_modificacion ? new Date(b.ultima_modificacion).getTime() : 0;
        return fechaB - fechaA;
      });
      setPacientes(pacientesOrdenados);
    } catch (error) {
      console.error('Error al cargar pacientes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calcular estadísticas
  const estadisticas: Estadisticas = useMemo(() => {
    const ahora = new Date();
    const hace24Horas = new Date(ahora.getTime() - 24 * 60 * 60 * 1000);
    
    return {
      total: pacientes.length,
      activos: pacientes.filter(p => {
        if (!p.ultima_modificacion) return false;
        const ultimaMod = new Date(p.ultima_modificacion);
        return ultimaMod > hace24Horas;
      }).length,
      nuevos: pacientes.filter(p => {
        if (!p.ultima_modificacion) return false;
        const ultimaMod = new Date(p.ultima_modificacion);
        return ultimaMod > hace24Horas;
      }).length,
      conEmails: pacientes.filter(p => p.email || p.email2).length,
    };
  }, [pacientes]);

  // Generar alfabeto para filtro rápido
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  
  // Obtener obras sociales únicas
  const obrasSociales = useMemo(() => {
    const obras = pacientes
      .map(p => p.obra_social)
      .filter(obra => obra && obra !== '0')
      .filter((obra, index, arr) => arr.indexOf(obra) === index)
      .sort();
    return obras;
  }, [pacientes]);

  // Función para normalizar texto (quitar acentos)
  const normalizarTexto = (texto: string): string => {
    return texto
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  // Filtrar pacientes
  const filteredPacientes = useMemo(() => {
    let filtered = pacientes;

    // Filtro por género desde URL
    if (generoFiltro) {
      if (generoFiltro === 'SinGenero') {
        filtered = filtered.filter(p => !p.genero || p.genero === '');
      } else {
        filtered = filtered.filter(p => p.genero === generoFiltro);
      }
    }

    // Filtro por letra
    if (selectedLetter) {
      filtered = filtered.filter(p => 
        p.nombre?.toUpperCase().startsWith(selectedLetter)
      );
    }

    // Filtro por búsqueda
    if (searchTerm) {
      const searchNormalized = normalizarTexto(searchTerm);
      filtered = filtered.filter(p =>
        normalizarTexto(p.nombre || '').includes(searchNormalized) ||
        normalizarTexto(p.email || '').includes(searchNormalized) ||
        normalizarTexto(p.email2 || '').includes(searchNormalized) ||
        normalizarTexto(p.obra_social || '').includes(searchNormalized)
      );
    }

    // Filtros avanzados
    if (filtros.obraSocial) {
      const obraSocialNormalized = normalizarTexto(filtros.obraSocial);
      filtered = filtered.filter(p => normalizarTexto(p.obra_social || '') === obraSocialNormalized);
    }

    if (filtros.genero) {
      const generoNormalized = normalizarTexto(filtros.genero);
      filtered = filtered.filter(p => normalizarTexto(p.genero || '') === generoNormalized);
    }

    if (filtros.dni) {
      const dniNormalized = normalizarTexto(filtros.dni);
      filtered = filtered.filter(p => normalizarTexto(p.dni || '').includes(dniNormalized));
    }

    if (filtros.profesion) {
      const profesionNormalized = normalizarTexto(filtros.profesion);
      filtered = filtered.filter(p => normalizarTexto(p.profesion || '').includes(profesionNormalized));
    }

    if (filtros.religion) {
      const religionNormalized = normalizarTexto(filtros.religion);
      filtered = filtered.filter(p => normalizarTexto(p.religion || '').includes(religionNormalized));
    }

    // Filtro por rango de edad
    filtered = filtered.filter(p => {
      if (!p.edad || p.edad === 0) return true;
      return p.edad >= filtros.rangoEdad[0] && p.edad <= filtros.rangoEdad[1];
    });

    // Ordenamiento
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'nombre':
          aValue = a.nombre || '';
          bValue = b.nombre || '';
          break;
        case 'edad':
          aValue = a.edad || 0;
          bValue = b.edad || 0;
          break;
        case 'obra_social':
          aValue = a.obra_social || '';
          bValue = b.obra_social || '';
          break;
        case 'ultima_modificacion':
          aValue = a.ultima_modificacion ? new Date(a.ultima_modificacion).getTime() : 0;
          bValue = b.ultima_modificacion ? new Date(b.ultima_modificacion).getTime() : 0;
          break;
        default:
          aValue = a.nombre || '';
          bValue = b.nombre || '';
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [pacientes, generoFiltro, selectedLetter, searchTerm, filtros, sortBy, sortDirection]);

  // Handlers
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleLetterClick = (letter: string) => {
    setSelectedLetter(selectedLetter === letter ? '' : letter);
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  const handleSortChange = (value: string) => {
    if (sortBy === value) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(value);
      setSortDirection('asc');
    }
    setSortAnchorEl(null);
  };

  const handleDeleteClick = (paciente: Paciente) => {
    setSelectedPaciente(paciente);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPaciente) return;
    
      try {
        await axios.delete(`${API_BASE_URL}/pacientes/${selectedPaciente.id}`);
      setPacientes(prev => prev.filter(p => p.id !== selectedPaciente.id));
        setDeleteDialogOpen(false);
        setSelectedPaciente(null);
      } catch (error) {
        console.error('Error al eliminar paciente:', error);
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      obraSocial: '',
      genero: '',
      rangoEdad: [0, 100],
      dni: '',
      profesion: '',
      religion: '',
    });
    setSearchTerm('');
    setSelectedLetter('');
  };

  const hasActiveFilters = () => {
    return searchTerm || 
           selectedLetter || 
           filtros.obraSocial || 
           filtros.genero || 
           filtros.dni ||
           filtros.profesion ||
           filtros.religion ||
           filtros.rangoEdad[0] !== 0 || 
           filtros.rangoEdad[1] !== 100;
  };

  // Función para obtener el estado del paciente
  const getPacienteStatus = (paciente: Paciente) => {
    const ultimaMod = paciente.ultima_modificacion ? new Date(paciente.ultima_modificacion) : null;
    const ahora = new Date();
    const hace24Horas = new Date(ahora.getTime() - 24 * 60 * 60 * 1000);
    
    // Solo retornar si es reciente (creado en las últimas 24 horas)
    if (ultimaMod && ultimaMod > hace24Horas) {
      return { status: 'reciente', color: 'warning', icon: <AccessTimeIcon /> };
    }
    
    // Si no es reciente, no retornar nada
    return null;
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: 'background.default', maxWidth: { xs: '100vw', sm: 'none' }, overflowX: { xs: 'hidden', sm: 'visible' } }}>
      {/* Header con estadísticas */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#1a202c', mb: 1, fontSize: { xs: '1.3rem', sm: '2.5rem' } }}>
              Gestión de Pacientes
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              Administra y visualiza todos los pacientes del sistema
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Cards de estadísticas - Muy pequeñas */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Paper sx={{ 
                p: 1, 
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'center',
                minWidth: 60
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {estadisticas.total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
              </Paper>
            </Box>

            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/pacientes/agregar')}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 4px 14px 0 rgba(0, 118, 255, 0.39)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px 0 rgba(0, 118, 255, 0.5)',
                }
              }}
            >
              Nuevo Paciente
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Barra de herramientas */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
          {/* Búsqueda */}
        <TextField
            placeholder="Buscar pacientes..."
          value={searchTerm}
          onChange={handleSearch}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
            sx={{ minWidth: 300, flexGrow: 1 }}
          />

          {/* Botón de filtros avanzados */}
          <Tooltip title="Filtros avanzados">
            <IconButton 
              onClick={() => setFiltrosAvanzados(!filtrosAvanzados)}
              sx={{ 
                bgcolor: filtrosAvanzados ? 'primary.main' : 'grey.100', 
                color: filtrosAvanzados ? 'white' : 'text.primary',
                '&:hover': { 
                  bgcolor: filtrosAvanzados ? 'primary.dark' : 'grey.200' 
                }
              }}
            >
              <FilterIcon />
            </IconButton>
          </Tooltip>

          {/* Botón de ordenamiento */}
          <Tooltip title="Ordenar por">
            <IconButton 
              onClick={(e) => setSortAnchorEl(e.currentTarget)}
              sx={{ 
                bgcolor: 'grey.100', 
                color: 'text.primary',
                '&:hover': { bgcolor: 'grey.200' }
              }}
            >
          <SortIcon />
        </IconButton>
          </Tooltip>
      </Box>

        {/* Filtro alfabético */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Filtro rápido por apellido:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant={selectedLetter === '' ? 'contained' : 'outlined'}
              onClick={() => setSelectedLetter('')}
              sx={{ minWidth: 40, borderRadius: 2 }}
            >
              Todos
            </Button>
            {alphabet.map((letter) => (
              <Button
                key={letter}
                size="small"
                variant={selectedLetter === letter ? 'contained' : 'outlined'}
                onClick={() => handleLetterClick(letter)}
                disabled={pacientes.filter(p => p.nombre?.toUpperCase().startsWith(letter)).length === 0}
                sx={{ 
                  minWidth: 40, 
                  borderRadius: 2,
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  }
                }}
              >
                {letter}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Filtros avanzados */}
        <Accordion 
          expanded={filtrosAvanzados} 
          onChange={() => setFiltrosAvanzados(!filtrosAvanzados)}
          sx={{ 
            mb: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            '&:before': { display: 'none' },
            boxShadow: 'none'
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                gap: 1
              }
            }}
          >
            <FilterIcon sx={{ color: 'text.secondary' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Filtros Avanzados
            </Typography>
            {hasActiveFilters() && (
              <Chip 
                label="Activos" 
                size="small" 
                color="primary" 
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Grid container spacing={3}>
              {/* Filtro por DNI */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="DNI"
                  placeholder="Buscar por DNI..."
                  value={filtros.dni || ''}
                  onChange={(e) => setFiltros(prev => ({ ...prev, dni: e.target.value }))}
                  size="small"
                />
              </Grid>

              {/* Filtro por género */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ 
                    transform: 'translate(14px, 8px) scale(1)',
                    '&.Mui-focused': {
                      transform: 'translate(14px, -9px) scale(0.75)'
                    },
                    '&.MuiFormLabel-filled': {
                      transform: 'translate(14px, -9px) scale(0.75)'
                    }
                  }}>
                    Género
                  </InputLabel>
                  <Select
                    name="genero"
                    value={filtros.genero}
                    onChange={handleFilterChange}
                    label="Género"
                    size="small"
                    sx={{
                      '& .MuiSelect-select': {
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Masculino">Masculino</MenuItem>
                    <MenuItem value="Femenino">Femenino</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Filtro por obra social */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ 
                    transform: 'translate(14px, 8px) scale(1)',
                    '&.Mui-focused': {
                      transform: 'translate(14px, -9px) scale(0.75)'
                    },
                    '&.MuiFormLabel-filled': {
                      transform: 'translate(14px, -9px) scale(0.75)'
                    }
                  }}>
                    Obra Social
                  </InputLabel>
                  <Select
                    name="obraSocial"
                    value={filtros.obraSocial}
                    onChange={handleFilterChange}
                    label="Obra Social"
                    size="small"
                    sx={{
                      '& .MuiSelect-select': {
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {obrasSociales.map((obra) => (
                      <MenuItem key={obra} value={obra}>{obra}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Filtro por profesión */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Profesión"
                  placeholder="Buscar por profesión..."
                  value={filtros.profesion || ''}
                  onChange={(e) => setFiltros(prev => ({ ...prev, profesion: e.target.value }))}
                  size="small"
                />
              </Grid>

              {/* Filtro por religión */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Religión"
                  placeholder="Buscar por religión..."
                  value={filtros.religion || ''}
                  onChange={(e) => setFiltros(prev => ({ ...prev, religion: e.target.value }))}
                  size="small"
                />
              </Grid>

              {/* Filtro por rango de edad */}
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Rango de Edad: {filtros.rangoEdad[0]} - {filtros.rangoEdad[1]} años
                  </Typography>
                  <Slider
                    value={filtros.rangoEdad}
                    onChange={(_, value) => setFiltros(prev => ({ ...prev, rangoEdad: value as number[] }))}
                    valueLabelDisplay="auto"
                    min={0}
                    max={100}
                    sx={{
                      '& .MuiSlider-thumb': {
                        backgroundColor: 'primary.main',
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: 'primary.main',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: 'grey.300',
                      }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Botón de limpiar filtros */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={limpiarFiltros}
                startIcon={<ClearIcon />}
                sx={{
                  borderColor: 'text.secondary',
                  color: 'text.secondary',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'text.secondary',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: 'text.secondary'
                  }
                }}
              >
                Limpiar
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Menú de ordenamiento */}
        <Menu anchorEl={sortAnchorEl} open={Boolean(sortAnchorEl)} onClose={() => setSortAnchorEl(null)}>
        <MenuItem onClick={() => handleSortChange('nombre')}>
          Nombre
          {sortBy === 'nombre' && (
            sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 1 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 1 }} />
          )}
        </MenuItem>
        <MenuItem onClick={() => handleSortChange('edad')}>
          Edad
          {sortBy === 'edad' && (
            sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 1 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 1 }} />
          )}
        </MenuItem>
        <MenuItem onClick={() => handleSortChange('obra_social')}>
          Obra Social
          {sortBy === 'obra_social' && (
            sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 1 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 1 }} />
          )}
        </MenuItem>
        <MenuItem onClick={() => handleSortChange('ultima_modificacion')}>
          Última Modificación
          {sortBy === 'ultima_modificacion' && (
            sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 1 }} /> : <ArrowDownwardIcon fontSize="small" sx={{ ml: 1 }} />
          )}
        </MenuItem>
      </Menu>

        {/* Resultados */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredPacientes.length} de {pacientes.length} pacientes
          </Typography>
        </Box>
      </Paper>

      {/* Grid de pacientes */}
      {loading ? (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Skeleton variant="circular" width={80} height={80} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="80%" height={32} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Skeleton variant="rectangular" width={60} height={24} />
                      <Skeleton variant="rectangular" width={60} height={24} />
                    </Box>
                    <Skeleton variant="text" width="100%" height={20} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filteredPacientes.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <InfoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No se encontraron pacientes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Intenta ajustar los filtros o agregar un nuevo paciente
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/pacientes/agregar')}
          >
            Agregar Paciente
          </Button>
        </Paper>
      ) : (
      <Grid container spacing={3}>
          {filteredPacientes.map((paciente, index) => {
            const status = getPacienteStatus(paciente);
            const isRecent = status?.status === 'reciente';
            return (
          <Grid item xs={12} sm={6} md={4} key={paciente.id}>
                <Fade in timeout={150 + index * 30}>
            <Card
              sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      position: 'relative',
                      border: isRecent ? '2px solid' : '1px solid',
                      borderColor: isRecent ? 'warning.main' : 'divider',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 12px 35px rgba(25, 118, 210, 0.25)',
                        backgroundColor: 'rgba(25, 118, 210, 0.02)',
                        borderColor: 'primary.main',
                      },
                    }}
              onClick={() => navigate(`/pacientes/${paciente.id}`)}
            >
                    {/* Badge de reciente solo */}
                    {isRecent && (
                      <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
                        <Chip
                          icon={<AccessTimeIcon />}
                          label="Reciente"
                          size="small"
                          color="warning"
                          sx={{ 
                            fontWeight: 600,
                            '& .MuiChip-icon': { fontSize: 16 }
                          }}
                        />
                      </Box>
                    )}

                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {/* Avatar */}
                        <Avatar
                          src={getFileUrl(paciente.ruta_imagen_perfil)}
                          sx={{ 
                            width: 80, 
                            height: 80, 
                            mb: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '2px solid',
                            borderColor: 'divider'
                          }}
                        >
                          {paciente.nombre ? 
                            paciente.nombre.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2) : 
                            '?'
                          }
                        </Avatar>

                        {/* Nombre */}
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600, 
                            mb: 1, 
                            textAlign: 'center',
                            color: 'text.primary'
                          }}
                        >
                  {paciente.nombre}
                </Typography>

                        {/* Chips de información */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
                  {paciente.obra_social && paciente.obra_social !== '0' && (
                            <Chip 
                              label={paciente.obra_social} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                            />
                  )}
                  {['Masculino', 'Femenino'].includes(paciente.genero) && (
                            <Chip 
                              label={paciente.genero} 
                              size="small" 
                              variant="outlined"
                              color="secondary"
                            />
                  )}
                  {paciente.edad && paciente.edad !== 0 ? (
                            <Chip 
                              label={`${paciente.edad} años`} 
                              size="small" 
                              variant="outlined"
                            />
                          ) : (
                            <Chip 
                              label="Sin edad" 
                              size="small" 
                              variant="outlined"
                              color="default"
                            />
                  )}
                </Box>

                        {/* Emails */}
                        <Box sx={{ width: '100%', mb: 2 }}>
                          {paciente.email && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                textAlign: 'center',
                                color: 'text.secondary',
                                fontWeight: 500,
                                mb: 0.5
                              }}
                            >
                  {paciente.email}
                </Typography>
                          )}
                {paciente.email2 && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      textAlign: 'center',
                                color: 'text.secondary',
                      fontSize: '0.8rem',
                      fontStyle: 'italic'
                    }}
                  >
                    {paciente.email2}
                  </Typography>
                )}
                        </Box>

                        {/* Última actividad */}
                        {paciente.ultima_modificacion && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary',
                              textAlign: 'center',
                              display: 'block',
                              mb: 2
                            }}
                          >
                            Última actividad: {new Date(paciente.ultima_modificacion).toLocaleDateString()}
                          </Typography>
                        )}

                        {/* Botones de acción - Reincorporados con colores originales */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          gap: 1, 
                          width: '100%',
                          mt: 1
                        }}>
                  <Tooltip title="Editar paciente">
                    <IconButton
                      color="primary"
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/pacientes/agregar?id=${paciente.id}`);
                      }}
                      sx={{
                        opacity: 0.8,
                        transition: 'all 0.2s',
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                          
                  <Tooltip title="Enviar email">
                    <IconButton
                      color="info"
                      onClick={e => {
                        e.stopPropagation();
                        localStorage.setItem('selectedPacienteForEmail', JSON.stringify(paciente));
                        navigate('/email');
                      }}
                      sx={{
                        opacity: 0.8,
                        transition: 'all 0.2s',
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: 'rgba(2, 136, 209, 0.08)',
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <EmailIcon />
                    </IconButton>
                  </Tooltip>
                          
                  <Tooltip title="Planes alimentarios">
                    <IconButton
                      color="success"
                      disabled
                      onClick={e => {
                        e.stopPropagation();
                        // navigate('/planes'); // Deshabilitado
                      }}
                      sx={{
                        opacity: 0.3,
                        transition: 'all 0.2s',
                        cursor: 'not-allowed',
                        '&:hover': {
                          opacity: 0.3,
                          backgroundColor: 'transparent',
                          transform: 'none'
                        },
                        '&.Mui-disabled': {
                          opacity: 0.3,
                          color: 'rgba(0, 0, 0, 0.38)'
                        }
                      }}
                    >
                      <RestaurantIcon />
                    </IconButton>
                  </Tooltip>
                          
                  <Tooltip title="Eliminar paciente">
                    <IconButton
                      color="error"
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteClick(paciente);
                      }}
                      sx={{
                        opacity: 0.8,
                        transition: 'all 0.2s',
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                        </Box>
                </Box>
              </CardContent>
            </Card>
                </Fade>
          </Grid>
            );
          })}
      </Grid>
      )}

      {/* Diálogo de confirmación de eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea eliminar al paciente {selectedPaciente?.nombre}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Pacientes; 